import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import PermissionGate from './PermissionGate';
import { getBusinessDashboardNavigation, NavigationSection } from '../utils/navigationUtils';
import { canAccessCustomersPage, isBusinessOwner, isBusinessOwnerForBusiness } from '../utils/roleUtils';
import { apiService } from '../services/api';
import { User } from '../types';

interface BusinessDashboardSidebarProps {
  isSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  activeSection?: string;
  onClose: () => void;
  onToggleCollapse: () => void;
  onSectionChange?: (section: string) => void;
  unreadNotificationCount?: number;
}

const BusinessDashboardSidebar: React.FC<BusinessDashboardSidebarProps> = ({
  isSidebarOpen,
  isSidebarCollapsed,
  activeSection,
  onClose,
  onToggleCollapse,
  onSectionChange,
  unreadNotificationCount = 0
}) => {
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();

  // Track which section is currently expanded (only one at a time for accordion behavior)
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const hasAutoExpanded = useRef(false);
  const [hideMyWorkSection, setHideMyWorkSection] = useState(false);
  const [hasQuotableService, setHasQuotableService] = useState(false);
  const [version, setVersion] = useState<string>('');
  const [buildDate, setBuildDate] = useState<string>('');

  // Detect mobile vs desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Reset expanded section when sidebar opens on mobile to ensure proper accordion behavior
  // But preserve the auto-expanded section if it was set
  useEffect(() => {
    if (isMobile && isSidebarOpen && !hasAutoExpanded.current) {
      // Only reset if we haven't auto-expanded yet
      setExpandedSection(null);
    }
  }, [isMobile, isSidebarOpen]);

  // Load version and build date from version.txt
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const response = await fetch('/version.txt', {
          cache: 'no-cache'
        });
        if (response.ok) {
          const text = await response.text();
          const lines = text.trim().split('\n');
          if (lines.length >= 1) {
            setVersion(lines[0].trim());
            if (lines.length >= 2) {
              setBuildDate(lines[1].trim());
            }
          }
        } else {
          console.warn('Failed to load version.txt:', response.status, response.statusText);
        }
      } catch (error) {
        console.warn('Could not load version.txt:', error);
      }
    };
    loadVersion();
  }, []);

  // Hide "MY WORK" section for single-employee businesses (owner is only professional)
  useEffect(() => {
    const userIsOwner = Boolean(user && isBusinessOwner(user));
    const userCanPerform = Boolean(user?.canPerformServices === true);

    if (!user || !currentBusiness?.shortName) {
      setHideMyWorkSection(false);
      return;
    }

    // Only applies to business owners who can perform services
    if (!userIsOwner || !userCanPerform) {
      setHideMyWorkSection(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const professionals = await apiService.getBusinessProfessionalsByShortName(currentBusiness.shortName);
        const isOwnerOnlyProfessional =
          Array.isArray(professionals) &&
          professionals.length === 1 &&
          professionals[0]?.id === user.id;

        if (!cancelled) setHideMyWorkSection(isOwnerOnlyProfessional);
      } catch (error) {
        // Fail open: if we can't determine, keep existing navigation.
        if (!cancelled) setHideMyWorkSection(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.canPerformServices, user?.roles?.length, currentBusiness?.shortName]);

  // Only show "Quote Requests" when the business has at least one quotable service
  useEffect(() => {
    if (!currentBusiness?.id) {
      setHasQuotableService(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const services = await apiService.getServices(currentBusiness.id);
        if (!cancelled) {
          const hasQuotable = Array.isArray(services) && services.some((s: any) => s?.quotable === true);
          setHasQuotableService(hasQuotable);
        }
      } catch {
        if (!cancelled) setHasQuotableService(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentBusiness?.id]);

  // Auto-expand appropriate section based on user type or activeSection on initial load
  useEffect(() => {
    if (!user) return;
    
    const navigationItemsRaw = getBusinessDashboardNavigation(user, currentBusiness?.name);
    const navigationItems = hideMyWorkSection
      ? navigationItemsRaw.filter(section => section.title !== 'MY WORK')
      : navigationItemsRaw;
    
    // If activeSection is provided, find and expand the section containing that item
    if (activeSection) {
      for (const section of navigationItems) {
        const hasActiveItem = section.items.some(item => item.id === activeSection);
        if (hasActiveItem && section.title) {
          setExpandedSection(section.title);
          hasAutoExpanded.current = true;
          return;
        }
      }
    }
    
    // If no activeSection or already auto-expanded, use default behavior
    if (hasAutoExpanded.current) return;
    
    // Check if user is a Business Owner
    const userIsBusinessOwner = isBusinessOwner(user);
    
    // Check if user is a Professional (has canPerformServices)
    const userIsProfessional = user?.canPerformServices === true;
    
    // Find the appropriate section to expand
    let sectionToExpand: string | null = null;
    
    if (userIsBusinessOwner) {
      // Business Owner: expand BUSINESS section
      const businessSection = navigationItems.find(section => section.title === 'BUSINESS');
      if (businessSection) {
        sectionToExpand = 'BUSINESS';
      }
    } else if (userIsProfessional) {
      // Professional (but not Business Owner): expand MY WORK section
      const myWorkSection = navigationItems.find(section => section.title === 'MY WORK');
      if (myWorkSection) {
        sectionToExpand = 'MY WORK';
      }
    }
    
    if (sectionToExpand) {
      setExpandedSection(sectionToExpand);
      hasAutoExpanded.current = true;
    }
  }, [user, currentBusiness?.name, activeSection, hideMyWorkSection]);

  if (!user) return null;

  const formatUtcDate = (value: any): string => {
    if (!value) return 'Unknown';
    const raw = String(value);
    const normalized =
      /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(raw) ? raw.replace(' ', 'T') : raw;
    const d = value instanceof Date ? value : new Date(normalized);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString(undefined, { timeZone: 'UTC' });
  };

  const navigationItemsRaw = getBusinessDashboardNavigation(user, currentBusiness?.name);
  const navigationItems: NavigationSection[] = (hideMyWorkSection
    ? navigationItemsRaw.filter(section => section.title !== 'MY WORK')
    : navigationItemsRaw
  ).map(section => ({
    ...section,
    items: (hasQuotableService ? section.items : section.items.filter(item => item.id !== 'quote-requests')).filter(
      (item) =>
        item.id !== 'customers' ||
        (user && currentBusiness?.id && canAccessCustomersPage(user, currentBusiness.id))
    ),
  }));

  const toggleSection = (sectionKey: string) => {
    setExpandedSection(prev => {
      // If clicking the same section, collapse it. Otherwise, expand the new section
      return prev === sectionKey ? null : sectionKey;
    });
  };

  const handleSectionClick = (sectionId: string) => {
    if (onSectionChange) {
      onSectionChange(sectionId);
    } else {
      navigate(`/dashboard/business?section=${sectionId}`);
    }
    onClose();
  };

  const getBusinessPublicWebsiteUrl = (): string | null => {
    const shortName = String(currentBusiness?.shortName || '').trim();
    if (!shortName) return null;
    return `/${shortName}`;
  };

  const renderIcon = (item: any, isActive: boolean = false) => {
    // Always prefer iconClass if it exists and is valid
    if (item.iconClass && typeof item.iconClass === 'string' && item.iconClass.trim().length > 0) {
      const iconColor = isActive ? 'text-[#0B4778]' : (item.iconColor || 'text-gray-600');
      // Ensure iconClass is properly formatted (should have 'bi' prefix)
      const iconClass = item.iconClass.trim();
      return <i className={`${iconClass} ${iconColor}`}></i>;
    }
    // Fallback to emoji icon if iconClass is missing or invalid
    return <span>{item.icon || '🛠️'}</span>;
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar Navigation */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50 shrink-0 bg-white shadow-sm border-r border-gray-200 
        transform transition-all duration-300 ease-in-out lg:transform-none
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isSidebarCollapsed ? 'lg:w-[74px]' : 'w-64'}
        h-screen lg:h-full lg:max-h-full
      `}>
        <div className="p-4 h-full flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold text-gray-900 transition-opacity duration-300 ${
              isSidebarCollapsed ? 'lg:opacity-0 lg:hidden' : ''
            }`}>
              Business Dashboard
            </h2>
            <div className="flex items-center space-x-2">
              {/* Mobile Close Button */}
              <button 
                onClick={onClose}
                className="lg:hidden p-1 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              {/* Desktop Collapse Button */}
              <button
                onClick={onToggleCollapse}
                className="hidden lg:block p-1 rounded-md hover:bg-gray-100"
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d={isSidebarCollapsed ? "M13 5l7 7-7 7M5 5l7 7-7 7" : "M11 19l-7-7 7-7m8 14l-7-7 7-7"} />
                </svg>
              </button>
            </div>
          </div>
          
          <nav className="space-y-1 flex-1 overflow-y-auto scrollbar-hide">
            {navigationItems.map((section, sectionIndex) => {
              const sectionKey = section.title || `section-${sectionIndex}`;
              const isCollapsed = expandedSection !== sectionKey;
              
              return (
                <div key={sectionIndex}>
                  {/* Section header */}
                  {section.title && (
                    <>
                      {/* Separator */}
                      <div className={`border-t border-gray-200 my-2 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}></div>
                      <button
                        onClick={() => toggleSection(sectionKey)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-lg transition-colors ${
                          isSidebarCollapsed ? 'lg:hidden' : ''
                        }`}
                      >
                        <span>{section.title}</span>
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isCollapsed ? '' : 'rotate-180'
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </>
                  )}
                  
                  {/* Section items */}
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    // On desktop when sidebar is collapsed, always show all items. On mobile, always respect accordion state.
                    // Otherwise, respect section collapse state.
                    (!section.title || !isCollapsed) ? 'max-h-[1000px]' :
                    (isMobile ? 'max-h-0' : (isSidebarCollapsed ? 'max-h-[1000px]' : 'max-h-0'))
                  }`}>
                    {section.items
                      .filter((item) => item.id !== 'settings') // Hide Settings item from sidebar
                      .map((item) => {
                  // AACORE PLATFORM: AACore Home → platform landing
                  // Browser: new tab. Installed PWA: same window (avoids a second PWA instance).
                  if (item.id === 'home') {
                    const runningAsPwa =
                      window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
                      (window.navigator as any).standalone === true;

                    return (
                      <Link
                        key={item.id}
                        to="/"
                        {...(runningAsPwa
                          ? {}
                          : { target: '_blank', rel: 'noopener noreferrer' })}
                        onClick={() => {
                          if (runningAsPwa) onClose();
                        }}
                        className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-700 hover:bg-gray-100"
                        title={isSidebarCollapsed ? item.label : undefined}
                      >
                        <span className="mr-3">{renderIcon(item, false)}</span>
                        <span className={`transition-opacity duration-300 ${
                          isSidebarCollapsed ? 'lg:opacity-0 lg:hidden' : ''
                        }`}>
                          {item.label}
                        </span>
                      </Link>
                    );
                  }

                  // AACORE PLATFORM: public booking website for this business
                  if (item.id === 'my-website') {
                    const websiteUrl = getBusinessPublicWebsiteUrl();
                    return (
                      <a
                        key={item.id}
                        href={websiteUrl || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          if (!websiteUrl) {
                            e.preventDefault();
                          }
                          onClose();
                        }}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                          websiteUrl
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed pointer-events-none'
                        }`}
                        title={
                          isSidebarCollapsed
                            ? item.label
                            : websiteUrl
                              ? undefined
                              : 'Set up your business short name to open your website'
                        }
                        aria-disabled={!websiteUrl}
                      >
                        <span className="mr-3">{renderIcon(item, false)}</span>
                        <span className={`transition-opacity duration-300 ${
                          isSidebarCollapsed ? 'lg:opacity-0 lg:hidden' : ''
                        }`}>
                          {item.label}
                        </span>
                      </a>
                    );
                  }
                  
                  return (
                    <PermissionGate key={item.id} permission={item.permission}>
                      <button
                        onClick={() => handleSectionClick(item.id)}
                        className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                          activeSection === item.id
                            ? 'bg-[#0B4778]/20 text-[#0B4778]'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        title={isSidebarCollapsed ? item.label : undefined}
                      >
                        <span className="mr-3">{renderIcon(item, activeSection === item.id)}</span>
                        <span className={`transition-opacity duration-300 ${
                          isSidebarCollapsed ? 'lg:opacity-0 lg:hidden' : ''
                        }`}>
                          {item.label}
                        </span>
                        {/* Notification badge for unread notifications */}
                        {item.id === 'notifications' && unreadNotificationCount > 0 && !isSidebarCollapsed && (
                          <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]">
                            {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                          </span>
                        )}
                      </button>
                    </PermissionGate>
                  );
                })}
                  </div>
                </div>
              );
            })}
          </nav>
          
          {/* Version and Build Date at bottom */}
          {(version || buildDate) && (
            <div className={`mt-auto pt-4 border-t border-gray-200 ${isSidebarCollapsed ? 'lg:hidden' : ''}`}>
              {currentBusiness?.id && isBusinessOwnerForBusiness(user, currentBusiness.id) && (
                <>
                  <div className="px-3 pb-3 text-xs text-gray-600">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-gray-700">Renewal On</span>
                      <span className="text-gray-700 text-right truncate">
                        {formatUtcDate((currentBusiness as any)?.validUntil)}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gray-200" />
                </>
              )}
              <div className="px-3 py-2 text-xs text-gray-500">
                {version && <div>Version: {version}</div>}
                {buildDate && <div>Build: {buildDate}</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BusinessDashboardSidebar;

